import * as fabric from "fabric";
import { useEffect, useRef } from "react";

export function useArrow() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arrowInstanceRef = useRef<any>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // --- Classe LineArrow locale, compatible Fabric 6.x ---
    class LineArrow extends fabric.Line {
      constructor(points: number[], options: fabric.ILineOptions = {}) {
        super(points, options);
        this.type = "lineArrow";
      }

      _render(ctx: CanvasRenderingContext2D) {
        super._render(ctx);

        if (this.width === 0 || this.height === 0 || !this.visible) return;

        ctx.save();

        const xDiff = this.x2 - this.x1;
        const yDiff = this.y2 - this.y1;
        const angle = Math.atan2(yDiff, xDiff);

        ctx.translate((this.x2 - this.x1) / 2, (this.y2 - this.y1) / 2);
        ctx.rotate(angle);

        ctx.beginPath();
        ctx.moveTo(10, 0);
        ctx.lineTo(-20, 15);
        ctx.lineTo(-20, -15);
        ctx.closePath();
        ctx.fillStyle = this.stroke as string;
        ctx.fill();

        ctx.restore();
      }
    }

    // --- Créer le canvas Fabric ---
    const canvas = new fabric.Canvas(canvasRef.current);
    fabricCanvasRef.current = canvas;

    // --- Classe Arrow locale ---
    class Arrow {
      canvas: fabric.Canvas;
      isDrawing: boolean = false;

      constructor(canvas: fabric.Canvas) {
        this.canvas = canvas;
        this.bindEvents();
      }

      bindEvents() {
        this.canvas.on("mouse:down", (o) => this.onMouseDown(o));
        this.canvas.on("mouse:move", (o) => this.onMouseMove(o));
        this.canvas.on("mouse:up", () => this.onMouseUp());
        this.canvas.on("object:moving", () => this.disable());
      }

      onMouseDown(o: fabric.IEvent) {
        this.enable();
        const pointer = this.canvas.getPointer(o.e);
        const points = [pointer.x, pointer.y, pointer.x, pointer.y];
        const line = new LineArrow(points, {
          strokeWidth: 5,
          fill: "red",
          stroke: "red",
          originX: "center",
          originY: "center",
          hasBorders: false,
          hasControls: false,
        });
        this.canvas.add(line).setActiveObject(line);
      }

      onMouseMove(o: fabric.IEvent) {
        if (!this.isEnable()) return;
        const pointer = this.canvas.getPointer(o.e);
        const activeObj = this.canvas.getActiveObject();
        if (activeObj) {
          activeObj.set({ x2: pointer.x, y2: pointer.y });
          activeObj.setCoords();
          this.canvas.requestRenderAll();
        }
      }

      onMouseUp() {
        this.disable();
      }

      isEnable() {
        return this.isDrawing;
      }

      enable() {
        this.isDrawing = true;
      }

      disable() {
        this.isDrawing = false;
      }
    }

    const arrowInstance = new Arrow(canvas);
    arrowInstanceRef.current = arrowInstance;

    // --- Cleanup ---
    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
      arrowInstanceRef.current = null;
    };
  }, []);

  return { canvasRef, fabricCanvasRef, arrowInstanceRef };
}
